import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const OTP_LENGTH = 6;

export interface OtpInputProps {
  onComplete: (otp: string) => void;
  onResend: () => void;
  cooldownSeconds?: number;
  error?: string | null;
  isLoading?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  onComplete,
  onResend,
  cooldownSeconds = 180,
  error,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(
    Array(OTP_LENGTH).fill(null)
  );

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const notify = useCallback(
    (updated: string[]) => {
      const value = updated.join("");
      if (value.length === OTP_LENGTH) {
        onComplete(value);
      }
    },
    [onComplete]
  );

  const handleChange = (index: number, raw: string) => {
    // Take only the last digit typed (handles key repeat)
    const digit = raw.replace(/\D/g, "").slice(-1);
    const updated = [...digits];
    updated[index] = digit;
    setDigits(updated);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    notify(updated);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        // Clear current cell
        const updated = [...digits];
        updated[index] = "";
        setDigits(updated);
      } else if (index > 0) {
        // Move to previous and clear it
        const updated = [...digits];
        updated[index - 1] = "";
        setDigits(updated);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;

    const updated = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) {
      updated[i] = pasted[i];
    }
    setDigits(updated);

    // Focus the next empty cell or the last filled cell
    const nextEmpty = updated.findIndex((d) => !d);
    const focusIndex = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty;
    inputRefs.current[focusIndex]?.focus();

    notify(updated);
  };

  const handleResend = () => {
    setDigits(Array(OTP_LENGTH).fill(""));
    setSecondsLeft(cooldownSeconds);
    onResend();
    setTimeout(() => inputRefs.current[0]?.focus(), 0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        width: "100%",
      }}
    >
      {/* Digit boxes */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 1.5 },
          justifyContent: "center",
        }}
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) =>
              handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)
            }
            onPaste={handlePaste}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            error={!!error}
            disabled={isLoading}
            className="notranslate"
            translate="no"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 1,
              style: {
                textAlign: "center",
                fontSize: "1.4rem",
                fontWeight: 600,
                padding: "10px 0",
              },
            }}
            sx={{
              width: { xs: 40, sm: 50 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
              },
            }}
          />
        ))}
      </Box>

      {/* Error message */}
      {error && (
        <Typography
          variant="body2"
          color="error"
          className="notranslate"
          translate="no"
          sx={{ textAlign: "center" }}
        >
          {error}
        </Typography>
      )}

      {/* Resend controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {secondsLeft > 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
            className="notranslate"
            translate="no"
          >
            {t("auth.resendIn", {
              seconds: secondsLeft >= 60
                ? `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`
                : String(secondsLeft),
            })}
          </Typography>
        ) : (
          <Button
            variant="text"
            size="small"
            onClick={handleResend}
            disabled={isLoading}
            className="notranslate"
            translate="no"
            sx={{ fontWeight: 600 }}
          >
            {isLoading ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                <span>{t("auth.resendCode")}</span>
              </Box>
            ) : (
              t("auth.resendCode")
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OtpInput;
